import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:5050",
});

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:5050",
  headers: {
    "Content-Type": "application/json",
  },
});

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Auth
export const loginUser = async (form) => {
  try {
    const res = await apiClient.post("/auth/login", form);
    return { success: true, token: res.data.token };
  } catch (err) {
    const message = err.response?.data?.message || "Login failed";
    return { success: false, message };
  }
};

export const registerUser = async (form) => {
  try {
    await apiClient.post("/auth/register", form);
    return { success: true, message: "Registered successfully" };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.message || "Registration failed",
    };
  }
};

export const verifyEmail = async (token) => {
  try {
    const res = await apiClient.get(`/auth/verify-email?token=${token}`);
    return { success: true, message: res.data.message };
  } catch (err) {
    console.error("Email verification error:", err);
    return {
      success: false,
      message: err.response?.data?.error || "Verification failed",
    };
  }
};

// ✅ New: Change Password
export const changePassword = async ({ currentPassword, newPassword }) => {
  try {
    await apiClient.post(
      "/auth/change-password",
      { currentPassword, newPassword },
      { headers: getAuthHeaders() }
    );
    return { success: true, message: "Password updated successfully" };
  } catch (err) {
    return {
      success: false,
      message:
        err.response?.data?.message ||
        "Error updating password. Please try again.",
    };
  }
};

// GPT Access
export const getMyGpts = async () => {
  try {
    const res = await apiClient.get("/access/my-gpts", {
      headers: getAuthHeaders(),
    });
    return { success: true, data: res.data };
  } catch {
    return { success: false, message: "Failed to fetch GPTs" };
  }
};

export const grantGptAccess = async (userId, gptId) => {
  try {
    await apiClient.post(
      "/access/grant",
      { userId, gptId },
      { headers: getAuthHeaders() }
    );
    return { success: true };
  } catch {
    return { success: false, message: "Failed to grant access" };
  }
};

export const revokeGptAccess = async (userId, gptId) => {
  try {
    await apiClient.delete("/access/revoke", {
      headers: getAuthHeaders(),
      data: { userId, gptId }, // 👈 important!
    });
    return { success: true };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.message || "Failed to revoke access",
    };
  }
};

// GPT Management
export const getAllUsers = async () => {
  try {
    const res = await apiClient.get("/users", { headers: getAuthHeaders() });
    return { success: true, data: res.data };
  } catch {
    return { success: false, message: "Failed to load users" };
  }
};

export const getAllGpts = async () => {
  try {
    const res = await apiClient.get("/gpts", { headers: getAuthHeaders() });
    return { success: true, data: res.data };
  } catch {
    return { success: false, message: "Failed to load GPTs" };
  }
};

export const createGpt = async (newGpt) => {
  try {
    await apiClient.post("/gpts", newGpt, { headers: getAuthHeaders() });
    return { success: true };
  } catch {
    return { success: false, message: "Failed to create GPT" };
  }
};

export const updateGpt = async (gptId, updatedGpt) => {
  try {
    const res = await apiClient.put(`/gpts/${gptId}`, updatedGpt, { headers: getAuthHeaders() });
    return { success: true, data: res.data };
  } catch (err) {
    return { 
      success: false, 
      message: err.response?.data?.error || "Failed to update GPT" 
    };
  }
};

export const deleteGpt = async (gptId) => {
  try {
    await apiClient.delete(`/gpts/${gptId}`, { headers: getAuthHeaders() });
    return { success: true };
  } catch (err) {
    return { 
      success: false, 
      message: err.response?.data?.error || "Failed to delete GPT" 
    };
  }
};

export const adminChangeUserPassword = async (userId, newPassword) => {
  try {
    const res = await apiClient.post(
      "/auth/admin-change-password",
      { userId, newPassword },
      { headers: getAuthHeaders() }
    );
    return { success: true, message: res.data.message };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.message || "Failed to change user password",
    };
  }
};

export const getUserGptsByAdmin = async (userId) => {
  try {
    const res = await apiClient.get(`/access/user-gpts/${userId}`, {
      headers: getAuthHeaders(),
    });

    return { success: true, data: res.data };
  } catch (err) {
    console.error("Error fetching user GPTs (admin):", err);
    return {
      success: false,
      message: err.response?.data?.message || "Failed to fetch user GPTs",
    };
  }
};

// Obtener GPTs del usuario logueado
export const getUserGpts = async () => {
  try {
    const res = await apiClient.get("/access/my-gpts", {
      headers: getAuthHeaders(),
    });
    return { success: true, data: res.data };
  } catch (err) {
    console.error("Error fetching user GPTs:", err);
    return {
      success: false,
      message: err.response?.data?.message || "Error al obtener GPTs del usuario",
    };
  }
};

// Admin: Crear nuevo usuario
export const createUserByAdmin = async (userData) => {
  try {
    await apiClient.post("/users", userData, {
      headers: getAuthHeaders(),
    });
    return { success: true, message: "Usuario creado exitosamente" };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.message || "Error al crear usuario",
    };
  }
};

// Chat Management
export const createChat = async (gptId, title, initialMessage) => {
  // Debug: Verificar parámetros recibidos
  console.log("API createChat - Parámetros recibidos:", { gptId, title, initialMessage });
  
  const headers = getAuthHeaders();
  console.log("API createChat - Headers de autenticación:", headers);
  console.log("API createChat - Token en localStorage:", localStorage.getItem("token"));
  
  try {
    // Cambiar la URL para usar /gpt en lugar de /chats
    const res = await apiClient.post(
      `/gpt/${gptId}`,
      { title, initialMessage },
      { headers }
    );
    return { success: true, data: res.data.chat };
  } catch (err) {
    console.error("API createChat - Error:", err.response?.data);
    return {
      success: false,
      message: err.response?.data?.message || "Error al crear chat",
    };
  }
};

export const getUserChats = async (gptId) => {
  try {
    const res = await apiClient.get(`/gpt/${gptId}`, {
      headers: getAuthHeaders(),
    });
    return { success: true, data: res.data.data };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.message || "Error al obtener chats",
    };
  }
};

export const getChat = async (gptId, conversationId) => {
  try {
    const res = await apiClient.get(`/gpt/${gptId}/${conversationId}`, {
      headers: getAuthHeaders(),
    });
    return { success: true, data: res.data.data };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.message || "Error al obtener chat",
    };
  }
};

export const addMessage = async (gptId, conversationId, role, content) => {
  try {
    const res = await apiClient.post(
      `/gpt/${gptId}/${conversationId}/messages`,
      { role, content },
      { headers: getAuthHeaders() }
    );
    return { success: true, data: res.data.data };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.message || "Error al agregar mensaje",
    };
  }
};

export const searchChats = async (gptId, searchTerm) => {
  try {
    const res = await apiClient.get(`/gpt/${gptId}/search?searchTerm=${searchTerm}`, {
      headers: getAuthHeaders(),
    });
    return { success: true, data: res.data.data };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.message || "Error al buscar chats",
    };
  }
};

export const closeChat = async (gptId, conversationId) => {
  try {
    const res = await apiClient.put(`/gpt/${gptId}/${conversationId}/close`, {}, {
      headers: getAuthHeaders(),
    });
    return { success: true, message: res.data.message };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.message || "Error al cerrar chat",
    };
  }
};

// Enviar mensaje al GPT y persistir conversación
export const sendMessageToGpt = async (gptId, conversationId, message, includeBrainContext = false) => {
  try {
    console.log("API sendMessageToGpt - URL que se va a enviar:", `/gpt/${gptId}/${conversationId}/send`);
    console.log("API sendMessageToGpt - includeBrainContext:", includeBrainContext);
    const res = await apiClient.post(
      `/gpt/${gptId}/${conversationId}/send`,
      { message, includeBrainContext },
      { headers: getAuthHeaders() }
    );
    return { success: true, data: res.data.data };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.message || "Error al enviar mensaje al GPT",
    };
  }
};

// Obtener o crear la conversación única por GPT/usuario
export const getOrCreateCurrentChat = async (gptId) => {
  try {
    const res = await apiClient.get(`/gpt/${gptId}/current`, {
      headers: getAuthHeaders(),
    });
    return { success: true, data: res.data.data };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.message || "Error al obtener conversación",
    };
  }
};

// Brain completion status
export const getBrainCompletionStatus = async () => {
  try {
    const res = await apiClient.get(`/brain/status`, { headers: getAuthHeaders() });
    return { success: true, data: res.data.data };
  } catch (err) {
    return { success: false, message: err.response?.data?.message || "Error al consultar estado del Brain" };
  }
};

// Brain Management
export const getUserBrain = async () => {
  try {
    const res = await apiClient.get("/brain", {
      headers: getAuthHeaders(),
    });
    return { success: true, data: res.data.data };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.message || "Error al obtener el Brain",
    };
  }
};

export const getBrainContext = async () => {
  try {
    const res = await apiClient.get("/brain/context", {
      headers: getAuthHeaders(),
    });
    return { success: true, data: res.data.data };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.message || "Error al obtener el contexto del Brain",
    };
  }
};

export const createBrain = async (title, description = "") => {
  try {
    const res = await apiClient.post(
      "/brain/brains",
      { title, description },
      { headers: getAuthHeaders() }
    );
    return { success: true, data: res.data.data };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.message || "Error al crear el Brain",
    };
  }
};

export const updateBrain = async (brainId, title, description = "") => {
  try {
    const res = await apiClient.put(
      `/brain/brains/${brainId}`,
      { title, description },
      { headers: getAuthHeaders() }
    );
    return { success: true, data: res.data.data };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.message || "Error al actualizar el Brain",
    };
  }
};

export const deleteBrain = async (brainId) => {
  try {
    const res = await apiClient.delete(`/brain/brains/${brainId}`, {
      headers: getAuthHeaders(),
    });
    return { success: true, message: res.data.message };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.message || "Error al eliminar el Brain",
    };
  }
};

export const addBrainEntry = async (brainId, title, type, content, imageData = null) => {
  try {
    const res = await apiClient.post(
      `/brain/brains/${brainId}/entries`,
      { title, type, content, imageData },
      { headers: getAuthHeaders() }
    );
    return { success: true, data: res.data.data };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.message || "Error al agregar la entrada",
    };
  }
};

export const updateBrainEntry = async (brainId, entryId, title, type, content, imageData = null) => {
  try {
    const res = await apiClient.put(
      `/brain/brains/${brainId}/entries/${entryId}`,
      { title, type, content, imageData },
      { headers: getAuthHeaders() }
    );
    return { success: true, data: res.data.data };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.message || "Error al actualizar la entrada",
    };
  }
};

export const deleteBrainEntry = async (brainId, entryId) => {
  try {
    const res = await apiClient.delete(
      `/brain/brains/${brainId}/entries/${entryId}`,
      { headers: getAuthHeaders() }
    );
    return { success: true, message: res.data.message };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.message || "Error al eliminar la entrada",
    };
  }
};

export const reorderBrains = async (brainOrders) => {
  try {
    const res = await apiClient.put(
      "/brain/brains/reorder",
      { brainOrders },
      { headers: getAuthHeaders() }
    );
    return { success: true, message: res.data.message };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.message || "Error al reordenar los Brains",
    };
  }
};

export const reorderBrainEntries = async (brainId, entryOrders) => {
  try {
    const res = await apiClient.put(
      `/brain/brains/${brainId}/entries/reorder`,
      { entryOrders },
      { headers: getAuthHeaders() }
    );
    return { success: true, message: res.data.message };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.message || "Error al reordenar las entradas",
    };
  }
};

// User Profile
export const getUserProfile = async () => {
  try {
    const res = await apiClient.get("/users/profile", {
      headers: getAuthHeaders()
    });
    return { success: true, data: res.data };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.message || "Error al obtener perfil del usuario",
    };
  }
};

// Expiration Management
export const getUsersWithExpiration = async () => {
  try {
    const res = await apiClient.get("/users/with-expiration", {
      headers: getAuthHeaders()
    });
    return { success: true, data: res.data };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.message || "Error al obtener usuarios con expiración",
    };
  }
};

export const getUsersExpiringSoon = async () => {
  try {
    const res = await apiClient.get("/users/expiring-soon", {
      headers: getAuthHeaders()
    });
    return { success: true, data: res.data };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.message || "Error al obtener usuarios que expiran pronto",
    };
  }
};

export const updateUserExpiration = async (userId, expirationData) => {
  try {
    const res = await apiClient.put(`/users/${userId}/expiration`, expirationData, {
      headers: getAuthHeaders()
    });
    return { success: true, data: res.data };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.message || "Error al actualizar fecha de expiración",
    };
  }
};

export default api;
